import { useCallback, useRef } from 'react'
import type { ProjectSelectionOption } from '~/components/task/TaskForm'
import { CommandType } from '~/protos/agents'
import type { RepositoryBranch } from './useCodeContext'

/**
 * A persisted snapshot of the in-progress task-creation form, so a soft reload
 * (or HMR during dev) restores the user's place instead of resetting the form.
 */
export interface TaskFormDraft {
  option?: ProjectSelectionOption
  repositories?: RepositoryBranch[]
  description?: string
}

const draftKey = (command: CommandType | undefined) =>
  `taskFormDraft:v1:${command ?? CommandType.NONE}`

function readDraft(key: string): TaskFormDraft | null {
  try {
    const raw = sessionStorage.getItem(key)
    return raw ? (JSON.parse(raw) as TaskFormDraft) : null
  } catch {
    return null
  }
}

/**
 * Remember the task-creation form's draft across reloads, keyed per command and
 * stored in sessionStorage (so it survives soft reloads / HMR but not a new tab).
 *
 * @param command the command whose form is being edited
 * @param enabled persist only for fresh task creation (not editing/example mode)
 * @returns the draft loaded once on mount, plus `saveDraft`/`clearDraft`
 */
export function useTaskFormDraft(command: CommandType | undefined, enabled: boolean) {
  const key = draftKey(command)

  // Load exactly once on first render so restoring doesn't fight live edits.
  const draftRef = useRef<TaskFormDraft | null | undefined>(undefined)
  if (draftRef.current === undefined) {
    draftRef.current = enabled ? readDraft(key) : null
  }

  const saveDraft = useCallback(
    (partial: Partial<TaskFormDraft>) => {
      if (!enabled) return
      try {
        const existing = readDraft(key) ?? {}
        sessionStorage.setItem(key, JSON.stringify({ ...existing, ...partial }))
      } catch {
        // sessionStorage may be unavailable (private mode / SSR) — drafts are best-effort.
      }
    },
    [key, enabled]
  )

  const clearDraft = useCallback(() => {
    try {
      sessionStorage.removeItem(key)
    } catch {
      // best-effort
    }
  }, [key])

  return { draft: draftRef.current, saveDraft, clearDraft }
}
